/**
 * Pure state machine for FloatingPanel (plan 15.4 state diagram).
 *
 *   hidden --show--> small (or the last visible state; bubble when narrow)
 *   small  --minimise | close | escape--> bubble
 *   bubble --open_bubble--> small (maximised when narrow)
 *   small  --maximise--> maximised
 *   maximised --close | escape--> small (bubble when narrow)
 *   maximised --minimise--> bubble
 *   any    --hide--> hidden
 */
import { PANEL_ACTIONS, PANEL_STATES } from "./floatingPanelConstants";

const { HIDDEN, BUBBLE, SMALL, MAXIMISED } = PANEL_STATES;
const VISIBLE_STATES = new Set([BUBBLE, SMALL, MAXIMISED]);

export const isVisibleState = (state) => VISIBLE_STATES.has(state);

/** Under the narrow breakpoint there is no floating frame: small shows as the bubble. */
export const effectivePanelState = (state, narrow = false) =>
  narrow && state === SMALL ? BUBBLE : state;

const showTarget = (restoreState, narrow) => {
  if (narrow) return BUBBLE;
  return restoreState === BUBBLE || restoreState === SMALL ? restoreState : SMALL;
};

/**
 * @param {string} state current panel state
 * @param {string} action one of PANEL_ACTIONS
 * @param {{narrow?: boolean, restoreState?: string}} [options]
 * @returns {string} next state (the same value when the action does not apply)
 */
export function nextPanelState(state, action, { narrow = false, restoreState = null } = {}) {
  const shown = effectivePanelState(state, narrow);
  switch (action) {
    case PANEL_ACTIONS.HIDE:
      return HIDDEN;
    case PANEL_ACTIONS.SHOW:
      return shown === HIDDEN ? showTarget(restoreState, narrow) : state;
    case PANEL_ACTIONS.OPEN_BUBBLE:
      if (shown !== BUBBLE) return state;
      return narrow ? MAXIMISED : SMALL;
    case PANEL_ACTIONS.MINIMISE:
      return shown === SMALL || shown === MAXIMISED ? BUBBLE : state;
    case PANEL_ACTIONS.MAXIMISE:
      return shown === SMALL ? MAXIMISED : state;
    case PANEL_ACTIONS.CLOSE:
    case PANEL_ACTIONS.ESCAPE:
      if (shown === MAXIMISED) return narrow ? BUBBLE : SMALL;
      if (shown === SMALL) return BUBBLE;
      return state;
    default:
      return state;
  }
}
